import { api } from './client';
import type { KycStatus } from '../types';

export type BackendEkycStatus = 'NOT_SUBMITTED' | 'PENDING' | 'VERIFIED' | 'NEED_MORE' | 'FAILED';
export type BackendFarmerStatus = 'ACTIVE' | 'PAUSED' | 'STOPPED';
export type BackendHtxRelationStatus = 'ACTIVE' | 'NEEDS_ATTENTION' | 'AT_RISK' | 'SUSPENDED' | 'INELIGIBLE';
export type BackendFarmerMembershipStatus = 'PENDING' | 'ACTIVE' | 'REJECTED';
export type BackendMembershipInitiator = 'FARMER' | 'HTX';
export type BackendHtxStatus = 'ACTIVE' | 'INACTIVE';

export interface BackendFarmer {
  _id: string;
  /** Optional — a profile HTX pre-created (Phase 1, "HTX tạo hồ sơ nông hộ
   * trước") has no User account yet, hence no userId, until one is linked. */
  userId?: string;
  /** Optional — a farmer can exist as a standalone account with no HTX yet. */
  htxId?: string;
  fullName: string;
  ekycStatus: BackendEkycStatus;
  subAccountId?: string;
  address?: string;
  code?: string;
  phone?: string;
  province?: string;
  district?: string;
  ward?: string;
  areaHa?: number;
  householdMembers?: number;
  mainCrop?: string;
  startDate?: string;
  status: BackendFarmerStatus;
  htxRelationStatus: BackendHtxRelationStatus;
  membershipStatus: BackendFarmerMembershipStatus;
  membershipRejectedReason?: string;
  membershipInitiatedBy?: BackendMembershipInitiator;
  createdWithoutAccount?: boolean;
  attachments?: FarmerAttachmentInput[];
  notes?: string;
  cccdMask?: string;
  ocrName?: string;
  ocrDob?: string;
  ocrGender?: string;
  ocrAddress?: string;
  ocrIssueDate?: string;
}

export interface BackendHtx {
  _id: string;
  name: string;
  code: string;
  address?: string;
  representativeUserId?: string;
  status: BackendHtxStatus;
  subAccountId?: string;
  phone?: string;
  email?: string;
  representativeName?: string;
  representativeTitle?: string;
  foundedDate?: string;
  memberCount?: number;
  mainCrops?: string;
  totalAreaHa?: number;
  ekycStatus?: 'NOT_SUBMITTED' | 'PENDING' | 'VERIFIED' | 'NEED_MORE' | 'FAILED';
}

export interface PendingEkycFarmer {
  _id: string;
  fullName: string;
  phone?: string;
  code?: string;
  cccdMask?: string;
  ocrName?: string;
}

export type PendingEkycForHtx = BackendEkycRecord & { farmer: PendingEkycFarmer | null };

export interface BackendEkycRecord {
  _id: string;
  subjectId: string;
  idFrontFileId: string;
  idBackFileId: string;
  selfieFileId: string;
  ocrResult?: Record<string, unknown>;
  decision: BackendEkycStatus;
  /** Submission time — the queue orders and displays by it. */
  createdAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  notes?: string;
}

const EKYC_STATUS_MAP: Record<BackendEkycStatus, KycStatus> = {
  // 'registered' is this app's "has an account, has not verified yet" state — the
  // one that renders the capture flow rather than a waiting screen.
  NOT_SUBMITTED: 'registered',
  PENDING: 'ekyc_pending',
  VERIFIED: 'ekyc_verified',
  NEED_MORE: 'ekyc_need_more',
  FAILED: 'ekyc_failed',
};

export function toFrontendKycStatus(status: BackendEkycStatus): KycStatus {
  return EKYC_STATUS_MAP[status];
}

const MEMBERSHIP_STATUS_MAP: Record<BackendFarmerMembershipStatus, 'Chờ xác nhận' | 'Đã xác nhận' | 'Từ chối'> = {
  PENDING: 'Chờ xác nhận',
  ACTIVE: 'Đã xác nhận',
  REJECTED: 'Từ chối',
};

export function toFrontendMembershipStatus(status: BackendFarmerMembershipStatus): 'Chờ xác nhận' | 'Đã xác nhận' | 'Từ chối' {
  return MEMBERSHIP_STATUS_MAP[status];
}

// ─── HTX ──────────────────────────────────────────────────────────────────

export async function createHtx(data: { name: string; code: string; address?: string }): Promise<BackendHtx> {
  return api.post<BackendHtx>('/htx', data);
}

export async function listHtx(): Promise<BackendHtx[]> {
  const res = await api.get<{ items: BackendHtx[] }>('/htx');
  return res.items;
}

export async function getHtxById(htxId: string): Promise<BackendHtx> {
  return api.get<BackendHtx>(`/htx/${htxId}`);
}

export async function updateHtxProfile(htxId: string, data: {
  address?: string;
  phone?: string;
  email?: string;
  representativeName?: string;
  representativeTitle?: string;
  foundedDate?: string;
  memberCount?: number;
  mainCrops?: string;
  totalAreaHa?: number;
}): Promise<BackendHtx> {
  return api.patch<BackendHtx>(`/htx/${htxId}`, data);
}

// ─── Farmer ───────────────────────────────────────────────────────────────

export async function getFarmerById(farmerId: string): Promise<BackendFarmer> {
  return api.get<BackendFarmer>(`/farmers/${farmerId}`);
}

export async function listFarmersByHtx(
  htxId: string,
  page = 1,
  pageSize = 25,
  membershipStatus?: BackendFarmerMembershipStatus,
): Promise<{ items: BackendFarmer[]; total: number; page: number; pageSize: number }> {
  const query = membershipStatus ? `&membershipStatus=${membershipStatus}` : '';
  return api.get(`/farmers/htx/${htxId}?page=${page}&pageSize=${pageSize}${query}`);
}

export async function createFarmerProfile(data: {
  userId: string;
  /** Optional — omit to create a standalone farmer profile with no HTX yet. */
  htxId?: string;
  fullName: string;
  cccd: string;
  address?: string;
  phone?: string;
  province?: string;
  district?: string;
  ward?: string;
  areaHa?: number;
  householdMembers?: number;
  mainCrop?: string;
  startDate?: string;
}): Promise<BackendFarmer> {
  return api.post<BackendFarmer>('/farmers', data);
}

export async function updateFarmerProfile(farmerId: string, data: {
  address?: string;
  phone?: string;
  province?: string;
  district?: string;
  ward?: string;
  areaHa?: number;
  householdMembers?: number;
  mainCrop?: string;
  notes?: string;
}): Promise<BackendFarmer> {
  return api.patch<BackendFarmer>(`/farmers/${farmerId}`, data);
}

export async function updateFarmerStatus(farmerId: string, data: {
  status?: BackendFarmerStatus;
  htxRelationStatus?: BackendHtxRelationStatus;
}): Promise<BackendFarmer> {
  return api.patch<BackendFarmer>(`/farmers/${farmerId}/status`, data);
}

/** HTX confirms or rejects a farmer's join request. */
export async function updateFarmerMembership(farmerId: string, decision: 'APPROVE' | 'REJECT', reason?: string): Promise<BackendFarmer> {
  return api.patch<BackendFarmer>(`/farmers/${farmerId}/membership`, { decision, reason });
}

/** HTX searches for a standalone farmer (no htxId yet) to invite — the backend
 * forces this to standalone-only for an HTX_STAFF caller regardless of params. */
export async function searchStandaloneFarmers(q: string): Promise<{ items: BackendFarmer[]; total: number }> {
  return api.get(`/farmers?q=${encodeURIComponent(q)}&standalone=true&pageSize=10`);
}

/** HTX invites a standalone farmer to join — the farmer must accept via
 * respondToHtxInvite before the HTX can manage them. */
export async function inviteFarmerToHtx(farmerId: string): Promise<BackendFarmer> {
  return api.post<BackendFarmer>(`/farmers/${farmerId}/htx-invite`, {});
}

export interface FarmerAttachmentInput {
  fileId: string;
  type: 'CCCD_FRONT' | 'CCCD_BACK' | 'LAND_TITLE' | 'OFFTAKE_CONTRACT' | 'OTHER';
  caption?: string;
}

/** Phase 1 "HTX tạo hồ sơ nông hộ trước": creates a Farmer with no User
 * account yet — htxId is always the caller's own HTX server-side. */
/** `warning` is set when the phone number matches an existing farmer already
 * in this HTX — surfaced so the caller can double-check before assuming a
 * genuinely new person, not blocked outright (a shared household phone is a
 * legitimate case too). */
export async function createFarmerProfileByHtx(data: {
  fullName: string;
  cccd?: string;
  address?: string;
  phone?: string;
  province?: string;
  district?: string;
  ward?: string;
  areaHa?: number;
  householdMembers?: number;
  mainCrop?: string;
  startDate?: string;
  attachments?: FarmerAttachmentInput[];
}): Promise<{ farmer: BackendFarmer; warning?: string }> {
  return api.post<{ farmer: BackendFarmer; warning?: string }>('/farmers/htx-created', data);
}

export async function addFarmerAttachment(farmerId: string, attachment: FarmerAttachmentInput): Promise<BackendFarmer> {
  return api.patch<BackendFarmer>(`/farmers/${farmerId}/attachments`, attachment);
}

/** HTX's own userId-less (profile-only) farmers — used to check a newly
 * self-registered join request against a profile HTX already pre-created. */
/** The HTX's account-less profiles that are probably the same person as a
 * pending join request, best first, each with why (CCCD / SĐT / Họ tên). */
export async function suggestProfileMatches(pendingFarmerId: string): Promise<Array<BackendFarmer & { matchReasons: string[] }>> {
  return api.get<Array<BackendFarmer & { matchReasons: string[] }>>(`/farmers/${pendingFarmerId}/profile-matches`);
}

export async function listProfileOnlyFarmers(htxId: string, q?: string): Promise<BackendFarmer[]> {
  return api.get<BackendFarmer[]>(`/farmers/htx/${htxId}/profile-only${q ? `?q=${encodeURIComponent(q)}` : ''}`);
}

/** Merges a newly self-registered farmer (duplicateFarmerId) into an
 * already-existing profile-only farmer (targetFarmerId) HTX pre-created.
 * `warning` is set when the duplicate had its own season/field setup —
 * reassigned onto the target (keeping its own id), not blocked, but worth a
 * human review since it may not match what HTX itself had configured. */
export async function mergeFarmerProfiles(duplicateFarmerId: string, targetFarmerId: string): Promise<{ farmer: BackendFarmer; warning?: string }> {
  return api.post<{ farmer: BackendFarmer; warning?: string }>(`/farmers/${duplicateFarmerId}/merge-into/${targetFarmerId}`, {});
}

export interface FarmerProfileHistoryEntry {
  _id: string;
  farmerId: string;
  changedBy: string;
  changedByRole: string;
  changes: Record<string, { from: unknown; to: unknown }>;
  createdAt?: string;
}

export async function getFarmerProfileHistory(farmerId: string): Promise<FarmerProfileHistoryEntry[]> {
  return api.get<FarmerProfileHistoryEntry[]>(`/farmers/${farmerId}/profile-history`);
}

/** The invited farmer accepts/declines an HTX's invite — self-service only. */
export async function respondToHtxInvite(farmerId: string, decision: 'ACCEPT' | 'DECLINE'): Promise<BackendFarmer> {
  return api.patch<BackendFarmer>(`/farmers/${farmerId}/htx-invite/respond`, { decision });
}

/** Farmer voluntarily leaves their current HTX — self-service only. */
export async function leaveHtx(farmerId: string): Promise<BackendFarmer> {
  return api.patch<BackendFarmer>(`/farmers/${farmerId}/leave-htx`, {});
}

// ─── eKYC review flow ─────────────────────────────────────────────────────

export async function submitEkyc(data: {
  farmerId: string;
  idFrontFileId: string;
  idBackFileId: string;
  selfieFileId: string;
  ocrResult?: Record<string, unknown>;
}): Promise<BackendEkycRecord> {
  return api.post<BackendEkycRecord>('/ekyc/submit', data);
}

/** htxId is taken server-side from the caller's own session. */
export async function submitHtxEkyc(data: {
  idFrontFileId: string;
  idBackFileId: string;
  selfieFileId: string;
  ocrResult?: Record<string, unknown>;
}): Promise<BackendEkycRecord> {
  return api.post<BackendEkycRecord>('/ekyc/submit-htx', data);
}

export async function listPendingEkyc(): Promise<BackendEkycRecord[]> {
  return api.get<BackendEkycRecord[]>('/ekyc/pending');
}

/** The caller's own latest submission. Null when they have never submitted. */
export async function myLatestEkyc(): Promise<BackendEkycRecord | null> {
  return api.get<BackendEkycRecord | null>('/ekyc/mine');
}

/** Pending eKYC for one cooperative's farmers, each joined to the farmer being
 * decided on. The unscoped /ekyc/pending above is operator-only — it spans every
 * cooperative. */
export async function listPendingEkycByHtx(htxId: string): Promise<PendingEkycForHtx[]> {
  return api.get<PendingEkycForHtx[]>(`/ekyc/pending/htx/${htxId}`);
}

export async function reviewEkyc(recordId: string, decision: BackendEkycStatus, notes?: string): Promise<BackendEkycRecord> {
  return api.post<BackendEkycRecord>(`/ekyc/${recordId}/review`, { decision, notes });
}

/** Every eKYC submission for this farmer, any decision, newest first — unlike
 * listPendingEkycByHtx this still returns something once the farmer is already
 * VERIFIED, so the uploaded ID/selfie photos stay reachable after review. */
export async function listEkycHistoryByFarmer(farmerId: string): Promise<BackendEkycRecord[]> {
  return api.get<BackendEkycRecord[]>(`/farmers/${farmerId}/ekyc-history`);
}

// ── The farmer's own cooperative membership (Thông tin tài khoản → Hợp tác xã)
//
// Reads and changes only the caller's own membership: every route resolves the
// farmer from the session, so none of them takes an id.

export interface MyHtxMembership {
  farmerId: string;
  htxId: string | null;
  htxName: string | null;
  htxCode: string | null;
  membershipStatus: BackendFarmerMembershipStatus;
  membershipInitiatedBy: BackendMembershipInitiator | null;
  membershipRejectedReason: string | null;
}

export async function getMyHtxMembership(): Promise<MyHtxMembership> {
  return api.get<MyHtxMembership>('/me/htx');
}

export async function requestJoinHtx(htxId: string): Promise<BackendFarmer> {
  return api.post<BackendFarmer>('/me/htx/join', { htxId });
}

/** Withdraws the farmer's own join request while it is still waiting. */
export async function cancelHtxJoinRequest(): Promise<BackendFarmer> {
  return api.post<BackendFarmer>('/me/htx/cancel', {});
}

export async function leaveMyHtx(): Promise<BackendFarmer> {
  return api.post<BackendFarmer>('/me/htx/leave', {});
}
